/* --------------------------------------------------------------------------
 *  CacheService  (Angular / RxJS / Capacitor-aware, Filesystem edition)
 *
 *  • Native (Capacitor) → saves each entry as its own file in
 *    <Cache>/cacheService/…, so there’s effectively no 5 MB ceiling.
 *  • Web browser → falls back to window.localStorage.
 * ------------------------------------------------------------------------ */

import {
  HttpBackend,
  HttpClient,
  HttpHeaders,
  HttpResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, from, of, defer } from "rxjs";
import { switchMap, tap, shareReplay, catchError } from "rxjs/operators";
import { NetworkService } from "./network.service";
import * as LZString from "lz-string";

import { Capacitor } from "@capacitor/core";
import { Filesystem, Directory, Encoding } from "@capacitor/filesystem";

/* ────────────────────────────── interfaces ─────────────────────────────── */
interface CachedResource {
  data: any;
  contentType: string;
}

interface StorageEngine {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  keys(): Promise<string[]>;
}

/* ─────────────────────────────── service ───────────────────────────────── */
@Injectable({ providedIn: "root" })
export class CacheService {
  private readonly cacheName = "resource-cache";
  private readonly storage: StorageEngine;
  private readonly directHttp: HttpClient;

  private readonly isNative = Capacitor.getPlatform() !== "web";
  private readonly basePath = "cacheService"; // root folder (native only)

  // ───────────────────── in-flight dedupe maps ─────────────────────
  private inFlightRequests = new Map<string, Observable<any>>();
  private inFlightRawPromises = new Map<string, Promise<any>>();
  private inFlightItemsPromises = new Map<string, Promise<any[]>>();

  constructor(
    private http: HttpClient,
    httpBackend: HttpBackend,
    private network: NetworkService
  ) {
    this.directHttp = new HttpClient(httpBackend);
    this.storage = this.buildStorageEngine();
  }

  private fromFile = (f: string) => decodeURIComponent(f.replace(/\.txt$/, ""));

  /* ───────────────────────── storage selector ──────────────────────────── */
  private buildStorageEngine(): StorageEngine {
    if (this.isNative) {
      const dir = Directory.Cache;
      const toFile = (k: string) =>
        `${this.basePath}/${encodeURIComponent(k.trim())}.txt`;

      let cachedKeys: string[] = [];
      let isStale = true;

      const refreshDir = async () => {
        try {
          const { files } = await Filesystem.readdir({
            path: this.basePath,
            directory: dir,
          });
          cachedKeys = files.map((f) => this.fromFile(f.name));
        } catch (e) {
          console.log("Cache keys refresh error:", e);
          cachedKeys = [];
        }
        isStale = false;
      };

      return {
        getItem: async (key) => {
          try {
            const result = await Filesystem.readFile({
              path: toFile(key.trim()),
              directory: dir,
              encoding: Encoding.UTF8,
            });
            return (result as { data: string }).data;
          } catch (err) {
            console.log("Cache get error:", err);
            return null;
          }
        },
        setItem: async (key, value) => {
          try {
            await Filesystem.writeFile({
              path: toFile(key.trim()),
              data: value,
              directory: dir,
              encoding: Encoding.UTF8,
              recursive: true,
            });
            isStale = true;
          } catch (e) {
            console.log("Cache set error:", e);
          }
        },
        removeItem: async (key) => {
          try {
            await Filesystem.deleteFile({
              path: toFile(key.trim()),
              directory: dir,
            });
            isStale = true;
          } catch (e) {
            console.log("Cache delete error:", e);
          }
        },
        keys: async () => {
          if (isStale) await refreshDir();
          return cachedKeys;
        },
      };
    }

    return {
      getItem: async (k) => localStorage.getItem(k.trim()),
      setItem: async (k, v) => localStorage.setItem(k.trim(), v),
      removeItem: async (k) => localStorage.removeItem(k.trim()),
      keys: async () => Object.keys(localStorage),
    };
  }

  /* ───────────────────── helper: unique cache key ────────────────────── */
  private cacheKey(url: string) {
    return `${this.cacheName}-${url.trim()}`;
  }

  appendCacheBustingUrl(url: string): string {
    return `${url}${url.includes("?") ? "&" : "?"}_=${Date.now()}`;
  }

  async doesKeyExist(key: string): Promise<boolean> {
    const fullKey = this.cacheKey(key.trim());
    const keys = await this.storage.keys();
    return keys.includes(fullKey);
  }

  async doesRawKeyExist(key: string): Promise<boolean> {
    const keys = await this.storage.keys();
    return keys.includes(key);
  }

  /* ───────────────────────── shared getResource core ───────────────────────── */
  private getResourceShared(
    url: string,
    refresh: boolean,
    cache: boolean
  ): Observable<any> {
    const execId =
      new Date().getTime() + "-" + Math.floor(Math.random() * 1000);
    console.log(
      "CacheService getResourceShared for " + url + " [execId: " + execId + "]"
    );
    console.log(
      "CacheService refresh: " + refresh + " [execId: " + execId + "]"
    );
    console.log("CacheService cache: " + cache + " [execId: " + execId + "]");

    const key = this.cacheKey(url) + "-" + refresh;

    if (this.inFlightRequests.has(key)) {
      return this.inFlightRequests.get(key)!;
    }

    const request$ = defer(async () => {
      const exists = await this.doesKeyExist(url);
      console.log(
        "CacheService exists for " +
          url +
          ": " +
          exists +
          " [execId: " +
          execId +
          "]"
      );

      if (!exists) return null;

      console.log(
        "CacheService getting raw for " + url + " [execId: " + execId + "]"
      );
      const raw = await this.storage.getItem(this.cacheKey(url));
      console.log(
        "CacheService found raw for " +
          url +
          ": " +
          raw +
          " [execId: " +
          execId +
          "]"
      );

      if (!raw) return null;

      try {
        console.log(
          "CacheService parsing raw for " + url + " [execId: " + execId + "]"
        );
        const parsed = JSON.parse(
          LZString.decompressFromUTF16(raw)
        ) as CachedResource;
        console.log(
          "CacheService parsed raw for " +
            url +
            ": " +
            parsed +
            " [execId: " +
            execId +
            "]"
        );
        console.log("Now we refresh: " + refresh + " [execId: " + execId + "]");
        if (refresh) {
          console.log(
            "CacheService refreshing cache for " +
              url +
              " [execId: " +
              execId +
              "]"
          );
          this.updateCacheInBackground(url);
        }
        return this.wrap(parsed);
      } catch (e) {
        console.warn(
          `Bad cache entry for ${url} [execId: ${execId}], refetching…`,
          e
        );
        await this.clearResource(url);
        return null;
      }
    }).pipe(
      switchMap((cached) => {
        if (cached) return of(cached);

        console.log(
          "Cache not found for " + url + ", fetching… [execId: " + execId + "]"
        );
        return this.fetchAndCache(url, cache);
      }),
      tap({
        finalize: () => this.inFlightRequests.delete(key),
        error: () => this.inFlightRequests.delete(key),
      }),
      shareReplay(1)
    );

    this.inFlightRequests.set(key, request$);
    return request$;
  }

  /* ───────────────────── public API ────────────────────────────── */
  getResource(url: string, cache = false): Observable<any> {
    console.log("CacheService getResource for " + url);
    return this.getResourceShared(url, cache, cache);
  }

  getResourceAndOnlyCacheIfNative(url: string): Observable<any> {
    console.log("CacheService getResourceAndOnlyCacheIfNative for " + url);
    console.log("CacheService isNative: " + this.isNative);
    return this.getResourceShared(url, this.isNative, this.isNative);
  }

  getResourceWithoutRefresh(url: string, cache = false): Observable<any> {
    console.log("CacheService getResourceWithoutRefresh for " + url);
    return this.getResourceShared(url, false, cache);
  }

  /* ─────────────────── low-level: HTTP + store result ──────────────────── */
  private fetchAndCache(url: string, cache: boolean): Observable<any> {
    console.log("CacheService fetchAndCache for " + url);
    const fullUrl = this.appendCacheBustingUrl(url);
    const client = url.includes("s3.amazonaws") ? this.directHttp : this.http;

    return client
      .get(fullUrl, {
        headers: new HttpHeaders({ "Cache-Control": "no-cache" }),
        responseType: "blob",
        observe: "response",
      })
      .pipe(
        switchMap((res: HttpResponse<Blob>) => this.handleResponse(res)),
        tap(async (parsed) => {
          if (cache && parsed) {
            try {
              const compressed = LZString.compressToUTF16(
                JSON.stringify(parsed)
              );
              console.log("CacheService storing cache for " + url);
              await this.storage.setItem(this.cacheKey(url), compressed);
            } catch (e) {
              console.error(`Failed to store cache for ${url}`, e);
            }
          }
        }),
        catchError((err) => {
          if (this.network.isNetworkError(err)) this.network.markInternetDown();
          console.error(`Fetch failed for ${url}`, err);
          return of(null);
        })
      );
  }

  /* ───────────────────── response → CachedResource ─────────────────────── */
  private handleResponse(
    response: HttpResponse<Blob>
  ): Observable<CachedResource> {
    const ct = response.headers.get("Content-Type") || "";

    if (ct === "application/json") {
      return from(
        response.body!.text().then((txt) => ({
          data: JSON.parse(txt),
          contentType: ct,
        }))
      );
    }

    if (ct.startsWith("text/") || ct === "image/svg+xml") {
      return from(
        response.body!.text().then((txt) => ({ data: txt, contentType: ct }))
      );
    }

    if (ct === "image/png" || ct === "image/jpeg") {
      return from(
        this.blobToBase64(response.body!).then((b64) => ({
          data: b64,
          contentType: ct,
        }))
      );
    }

    return from(
      response.body!.text().then((txt) => ({ data: txt, contentType: ct }))
    );
  }

  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result as string);
      r.onerror = reject;
      r.readAsDataURL(blob);
    });
  }

  private wrap(p: CachedResource) {
    return { data: p.data, contentType: p.contentType };
  }

  /* ─────────────── background refresh (fire-and-forget) ────────────────── */
  private updateCacheInBackground(url: string) {
    console.log("CacheService updateCacheInBackground for " + url);
    this.fetchAndCache(url, true).subscribe();
  }

  /* ─────────────────────────── cache helpers ───────────────────────────── */
  async clearResource(url: string) {
    await this.storage.removeItem(this.cacheKey(url));
  }

  async clearAllResources() {
    const keys = await this.storage.keys();
    await Promise.all(
      keys
        .filter((k) => k.startsWith(this.cacheName))
        .map((k) => this.storage.removeItem(k))
    );
  }

  async isResourceCached(url: string) {
    return (await this.storage.getItem(this.cacheKey(url))) !== null;
  }

  /* ─────────── arbitrary JSON helpers ───────────────────────────── */
  async setCachedData(key: string, value: any) {
    try {
      const compressed = LZString.compressToUTF16(JSON.stringify(value));
      await this.storage.setItem(key, compressed);
    } catch (e) {
      console.error(`Failed to cache data for ${key}`, e);
    }
  }

  async getCachedData(key: string): Promise<any | null> {
    if (this.inFlightRawPromises.has(key)) {
      return this.inFlightRawPromises.get(key)!;
    }

    const promise = (async () => {
      if (!(await this.doesRawKeyExist(key))) return null;

      const raw = await this.storage.getItem(key);
      if (!raw) return null;

      try {
        return JSON.parse(LZString.decompressFromUTF16(raw));
      } catch {
        console.warn(
          `Cache getCachedData: Bad cache entry for ${key}, clearing it out`
        );
        await this.storage.removeItem(key);
        return null;
      } finally {
        this.inFlightRawPromises.delete(key);
      }
    })();

    this.inFlightRawPromises.set(key, promise);
    return promise;
  }

  async getCachedItems(filter: (k: string) => boolean): Promise<any[]> {
    const filterKey = filter.toString();
    if (this.inFlightItemsPromises.has(filterKey)) {
      return this.inFlightItemsPromises.get(filterKey)!;
    }

    const promise = (async () => this.collectCached(filter))();
    promise.finally(() => this.inFlightItemsPromises.delete(filterKey));
    this.inFlightItemsPromises.set(filterKey, promise);
    return promise;
  }

  /* ───────────────────────── internal helpers ─────────────────────────── */
  private async collectCached(
    predicate: (k: string) => boolean
  ): Promise<any[]> {
    const keys = (await this.storage.keys()).filter(
      (k) => k.startsWith(this.cacheName) && predicate(k)
    );

    const results = await Promise.all(
      keys.map(async (k) => {
        try {
          const raw = await this.storage.getItem(k);
          if (!raw) return null;
          const parsed = JSON.parse(
            LZString.decompressFromUTF16(raw)
          ) as CachedResource;
          return this.wrap(parsed);
        } catch (e) {
          console.error(`Bad cache at ${k}`, e);
          return null;
        }
      })
    );

    return results.filter(Boolean) as any[];
  }
}
